import { initializeApp, getApps, getApp, FirebaseApp } from '@firebase/app';
// @ts-expect-error - getReactNativePersistence is exported from the RN build but not present in the web types
import { getAuth, initializeAuth, getReactNativePersistence, Auth } from '@firebase/auth';
import {
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  Firestore,
} from '@firebase/firestore';
import { getStorage, FirebaseStorage } from '@firebase/storage';
import { getFunctions, Functions } from '@firebase/functions';
import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { createPrefixedLogger } from '../utils/logger';

const log = createPrefixedLogger('firebase');

// Expo Metro only inlines process.env.EXPO_PUBLIC_* when the reference is
// STATIC — dynamic lookups like process.env[v] don't get replaced at build
// time and are undefined at runtime on web. So reference each var directly.
const missingVars: string[] = [];
if (!process.env.EXPO_PUBLIC_FIREBASE_API_KEY) missingVars.push('EXPO_PUBLIC_FIREBASE_API_KEY');
if (!process.env.EXPO_PUBLIC_PROJECT_ID) missingVars.push('EXPO_PUBLIC_PROJECT_ID');
if (missingVars.length > 0) {
  throw new Error(
    `Missing required Firebase env vars: ${missingVars.join(', ')}. ` +
      'Copy .env.dev to .env for local development.'
  );
}

const firebaseConfig = {
  apiKey: process.env.EXPO_PUBLIC_FIREBASE_API_KEY!,
  authDomain: process.env.EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN ?? '',
  databaseURL: process.env.EXPO_PUBLIC_DATABASE_URL ?? '',
  projectId: process.env.EXPO_PUBLIC_PROJECT_ID!,
  storageBucket: process.env.EXPO_PUBLIC_STORAGE_BUCKET ?? '',
  messagingSenderId: process.env.EXPO_PUBLIC_MESSAGING_SENDER_ID ?? '',
  appId: process.env.EXPO_PUBLIC_APP_ID ?? '',
  measurementId: process.env.EXPO_PUBLIC_MEASUREMENT_ID ?? '',
};

let app: FirebaseApp;
let auth: Auth;

if (getApps().length === 0) {
  app = initializeApp(firebaseConfig);
} else {
  app = getApp();
}

if (Platform.OS === 'web') {
  auth = getAuth(app);
} else {
  try {
    auth = initializeAuth(app, {
      persistence: getReactNativePersistence(AsyncStorage),
    });
  } catch {
    // Auth already initialized (hot reload), get existing instance
    auth = getAuth(app);
  }
}

// Initialize Firestore with offline persistence. On web, opt into the
// IndexedDB-backed persistent cache with multi-tab support so queued writes
// survive reloads and sync across tabs. On native, the JS SDK has NO
// persistent cache (memory-only — there's no IndexedDB in RN), so every
// fresh launch pays full initial snapshots for all listeners; plain
// getFirestore is all that's available.
let db: Firestore;
if (Platform.OS === 'web') {
  try {
    // Firestore's default WebChannel streaming is unreliable under Playwright's
    // bundled WebKit (the audit:webkit smoke suite): the first onSnapshot
    // frequently stalls, so screens hang on "Loading…". Real iOS Safari is
    // unaffected, so we don't force long-polling for everyone — the WebKit
    // audit opts in via this localStorage flag (set before any app code runs).
    // The flag is absent in every real session, so production behavior is
    // unchanged and this branch is dead code there.
    let e2eForceLongPolling = false;
    try {
      e2eForceLongPolling = window.localStorage?.getItem('redi:e2eForceLongPolling') === '1';
    } catch {
      // localStorage can throw in locked-down contexts — treat as opt-out.
    }
    db = initializeFirestore(app, {
      localCache: persistentLocalCache({
        tabManager: persistentMultipleTabManager(),
      }),
      ...(e2eForceLongPolling ? { experimentalForceLongPolling: true } : {}),
    });
  } catch (err) {
    // Already initialized (hot reload) or IndexedDB unsupported — fall back.
    log.warn('persistent cache unavailable, falling back:', err);
    db = getFirestore(app);
  }
} else {
  db = getFirestore(app);
}

const storage: FirebaseStorage = getStorage(app);

const functions: Functions = getFunctions(app);

export { app, auth, db, storage, functions };
